import React from 'react';
import { Box, Typography } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { CheckboxCard } from 'components/UI/CheckboxCard/CheckboxCard';
import { ClientPaymentMethod } from '@spike/payments-model';
import getCardIcon from 'hooks/useCardIcon';
import clsx from 'clsx';
import { useStyles } from './SelectableCreditCard.styles';

interface SelectableCreditCardProps {
    card: ClientPaymentMethod;
    selected: boolean;
    onSelect: (card: ClientPaymentMethod) => void;
    disabled?: boolean;
}

const SelectableCreditCard: React.FC<SelectableCreditCardProps> = ({ card, selected, onSelect, disabled = false }) => {
    const classes = useStyles();

    const isExpired = (expirationMonth: string, expirationYear: string): boolean => {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;

        const cardYear =
            expirationYear.length === 2 ? parseInt(expirationYear, 10) + 2000 : parseInt(expirationYear, 10);
        const cardMonth = parseInt(expirationMonth, 10);

        return cardYear < currentYear || (cardYear === currentYear && cardMonth < currentMonth);
    };

    const isCardExpired = isExpired(card.expirationMonth.toString(), card.expirationYear.toString());

    return (
        <CheckboxCard
            key={card.id}
            id={`card-${card.id}`}
            checked={selected}
            onChange={() => onSelect(card)}
            clickable={!isCardExpired && !disabled}
            className={classes.checkboxCard}
        >
            <Box className={classes.cardPrimaryInfoWrapper}>
                <Box className={classes.cardNumberWrapper}>
                    <img
                        src={getCardIcon(card.name)}
                        alt="Card icon"
                    />
                    <Typography className={classes.cardNumber}>•••• {card.lastDigits}</Typography>
                </Box>
                <Box>
                    {card.primary && (
                        <Box className={classes.primaryBadgeWrapper}>
                            <Box className={clsx(classes.defaultBadge, classes.primaryBadge)}>Primary</Box>
                        </Box>
                    )}
                </Box>
            </Box>
            <Box className={classes.cardExpirationWrapper}>
                <Box className={classes.cardExpirationText}>
                    Exp. {card.expirationMonth}/{card.expirationYear}
                </Box>

                {isCardExpired && (
                    <span className={classes.expired}>
                        <FontAwesomeIcon icon={faCircleExclamation} /> Expired
                    </span>
                )}
            </Box>
        </CheckboxCard>
    );
};

export default SelectableCreditCard;
